import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useResolvedPath } from 'react-router-dom';

import { useResizeObserver } from 'usehooks-ts';

import { Modal, Table, Row, Col, Input, notification } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LoadingOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import { exception, securityRestriction, success } from '@extensions/notification';
import { delayAction } from '@extensions/utils';

import { userLoaded, setFilter } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import { IUserSession } from '@entities/user-session';
import { ICity } from '@entities/city';
import { ICityFilter } from '@entities/city-filter';

import Processing from '@controls/processing';

import { Permission, hasPermission } from '@enums/permission';

const filterContext: string = 'Cities';

const Cities = (props: IBaseComponentProps) => {
    const { serverFetch } = props;

    const initFilter: ICityFilter = {};

    const navigate = useNavigate();
    const resolved = useResolvedPath('');

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const [api, contextHolder] = notification.useNotification();
    const [modal, modalContextHolder] = Modal.useModal();

    const filter = useAppSelector<ICityFilter>((s) => s.filters[filterContext]);

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [cities, setCities] = useState<Array<ICity>>([]);

    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [syncCities, setSyncCities] = useState<boolean>(false);

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);

        setCities([]);

        setLoading(true);

        serverFetch('cities', { method: 'GET', queryParams: filter })
            .then((data: Array<ICity>) => {
                setCities(data);
                setLoading(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));

            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onSync = () => {
        setSyncCities(true);

        serverFetch(`cities/sync`, { method: 'POST' })
            .then(() => {
                success(api, 'Синхронизация городов', `Успешно`);
                setSyncCities(false);
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                setSyncCities(false);
                exception(api, 'Ошибка синхронизации городов', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            style={{ width: 300 }}
                            key='searchText'
                            placeholder='Название'
                            value={filter?.searchText}
                            onChange={(data) => {
                                d(setFilter({ ...filter, searchText: data.target.value }, filterContext));
                            }}
                        />,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Синхронизировать города',
                        type: 'primary',
                        key: 'sync',
                        disabled: loading,
                        icon: <SyncOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageCities)) {
                                securityRestriction(api, [Permission.ManageCities]);
                                return;
                            }
                            onSync();
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<ICity> = [
            {
                title: 'Название',
                dataIndex: 'name',
                width: 200,
            },
            {
                title: 'Расстояние от Москвы / км',
                dataIndex: 'distance',
                align: 'center',
                width: 200,
            },
            {},
        ];

        const tableLoading = {
            spinning: loading,
            indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
        };

        return (
            <Table
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={cities}
                loading={tableLoading}
                pagination={false}
                scroll={{ y: height ?? 0 }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 192px - ${filterHeight}px)` }}>
            <Row>
                <Col span={9}>
                    {renderToolbar()}
                    {renderFilter()}
                    {renderTable()}
                    {syncCities && <Processing title='Синихронизация городов...' />}
                </Col>
            </Row>

            {contextHolder}
            {modalContextHolder}
        </div>
    );
};

export default Cities;
